import { useEffect, useState } from "react";

export interface MobileLayoutState {
  isMobile: boolean;        // < 768px
  isSmallMobile: boolean;   // < 480px (iPhone SE)
  isTouchDevice: boolean;   // pointer: coarse
  isPortrait: boolean;      // orientation: portrait
}

const MOBILE_BREAKPOINT = 768;
const SMALL_MOBILE_BREAKPOINT = 480;

/**
 * Hook to detect mobile layout states
 *
 * @returns {MobileLayoutState} Object containing mobile state booleans
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { isMobile, isSmallMobile, isTouchDevice, isPortrait } = useMobileLayout();
 *
 *   return isMobile ? <MobileView /> : <DesktopView />;
 * }
 * ```
 */
export function useMobileLayout(): MobileLayoutState {
  const [state, setState] = useState<MobileLayoutState>(() => {
    if (typeof window === "undefined") {
      return {
        isMobile: false,
        isSmallMobile: false,
        isTouchDevice: false,
        isPortrait: false,
      };
    }

    return {
      isMobile: window.innerWidth < MOBILE_BREAKPOINT,
      isSmallMobile: window.innerWidth < SMALL_MOBILE_BREAKPOINT,
      isTouchDevice: window.matchMedia("(pointer: coarse)").matches,
      isPortrait: window.matchMedia("(orientation: portrait)").matches,
    };
  });

  useEffect(() => {
    if (typeof window === "undefined") return;

    const updateLayout = () => {
      setState({
        isMobile: window.innerWidth < MOBILE_BREAKPOINT,
        isSmallMobile: window.innerWidth < SMALL_MOBILE_BREAKPOINT,
        isTouchDevice: window.matchMedia("(pointer: coarse)").matches,
        isPortrait: window.matchMedia("(orientation: portrait)").matches,
      });
    };

    // iOS Safari: innerWidth lags the orientationchange event. Re-evaluate on
    // the next frame and one tick later, so we don't latch onto the stale
    // landscape dimensions after rotating back to portrait.
    const updateLayoutAfterFrame = () => {
      requestAnimationFrame(updateLayout);
      setTimeout(updateLayout, 100);
    };

    // Run once to flush any stale init.
    updateLayout();

    window.addEventListener("resize", updateLayout);
    window.addEventListener("orientationchange", updateLayoutAfterFrame);

    // Also keep the MQ-change listener — fires reliably on desktop browsers
    // even when resize doesn't (e.g. devtools docked panel resize).
    const orientationQuery = window.matchMedia("(orientation: portrait)");
    if (orientationQuery.addEventListener) {
      orientationQuery.addEventListener("change", updateLayout);
    } else {
      orientationQuery.addListener(updateLayout);
    }

    return () => {
      window.removeEventListener("resize", updateLayout);
      window.removeEventListener("orientationchange", updateLayoutAfterFrame);
      if (orientationQuery.removeEventListener) {
        orientationQuery.removeEventListener("change", updateLayout);
      } else {
        orientationQuery.removeListener(updateLayout);
      }
    };
  }, []);

  return state;
}
